import { Component, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';
import { EcThresholdSetupService } from './ec-threshold-setup.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-ec-threshold-setup-ce',
    templateUrl: './ec-threshold-setup.component.html',
    styleUrls: ['./ec-threshold-setup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EcThresholdSetupComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  rowData: any = {};
  headerData: any = {};

  // Global service references
  globalService: any;
  USER_SETTINGS: any;
  GENERAL_CONFIG: any;
  SERVER_CONFIG: any;

  thresholdForm: UntypedFormGroup;
  isSaving = false;
  isSavingUpdate = false;
  isSavingDelete = false;
  showEntityLoader = false;
  minimise = false;
  modalTitle = 'Threshold Management';

  // Year Picker properties
  fromTaxYear: number | null = null;
  toTaxYear: number | null = null;
  showFromYearPicker: boolean = false;
  showToYearPicker: boolean = false;

  // Threshold entity data
  thresholdEntity: any = {};
  thresholdEntity_bckup: any = {};

  // Data arrays
  allTableClasses: any[] = [];
  allThresholdValues: any[] = [];
  allRefreshTypes: any[] = [];
  allTableTypes: any[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private ecThresholdSetupService: EcThresholdSetupService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {
    this.thresholdForm = this.fb.group({
      TAX_YEAR: [''],
      TABLE_ID: [''],
      SCHEMA_OWNER: ['', Validators.required],
      TABLE_NAME: ['', Validators.required],
      REFRESH_TYPE: ['', Validators.required],
      FROM_TAX_YEAR: ['', Validators.required],
      TO_TAX_YEAR: ['', Validators.required],
      TABLE_CLASSIFICATION: ['', Validators.required],
      SOURCE_SCHEMA: ['', Validators.required],
      SOURCE_TABLE_NAME: ['', Validators.required],
      TABLE_TYPE: ['', Validators.required],
      SRC_TO_TGT_VAR_THRESHOLD: ['', Validators.required],
      CLASSIFICATION_KEY: [''],
      CLASSIFICATION_DESC: ['']
    });
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(
      this.thresholdForm.valueChanges.subscribe((formValues) => {
        this.thresholdEntity = { ...this.thresholdEntity, ...formValues };
        this.cdr.markForCheck();
      })
    );
    this.init();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.headerData = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  init(): void {
    if (this.GENERAL_CONFIG?.base_url || this.baseURLs?.api) {
      this.loadInitialData();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  loadInitialData(): void {
    this.showEntityLoader = true;
    
    // Check if we're in edit mode (rowData exists and has TABLE_ID)
    if (this.rowData && this.rowData.TABLE_ID) {
      // Edit mode - populate form with existing data
      this.populateFormForEdit();
    } else {
      // Create mode - set default values
      this.populateFormForCreate();
    }

    // Load dropdown data
    this.loadThresholdRefreshType();
    this.loadThresholdClassification();
    this.loadThresholdTableType();
    this.loadThresholdValue();
    
    this.showEntityLoader = false;
  }

  /**
   * Normalize year value from various formats (Date objects, date strings, numbers)
   * Handles Dec 31 dates as next tax year
   */
  private normalizeYear(value: any): number | null {
    try {
      if (value instanceof Date) {
        const y = value.getUTCFullYear();
        const m = value.getUTCMonth(); // 0-11
        const d = value.getUTCDate();
        if (m === 11 && d === 31) { // Dec 31 → treat as next tax year
          return y + 1;
        }
        return y;
      }
      if (typeof value === 'number') {
        return value;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        // Check if it's a date string (ISO format)
        if (trimmed.includes('T') || trimmed.includes('-')) {
          const date = new Date(trimmed);
          if (!isNaN(date.getTime())) {
            const y = date.getUTCFullYear();
            const m = date.getUTCMonth(); // 0-11
            const d = date.getUTCDate();
            // If Dec 31, treat as next tax year
            if (m === 11 && d === 31) {
              return y + 1;
            }
            // Also check for year-end dates in string format
            if (/\-12\-31/i.test(trimmed)) {
              return y + 1;
            }
            return y;
          }
        }
        // Try to parse as number
        const yearMatch = trimmed.match(/(\d{4})/);
        const yr = yearMatch ? parseInt(yearMatch[1], 10) : NaN;
        if (!isNaN(yr) && yr >= 1900 && yr <= 2100) {
          return yr;
        }
      }
    } catch (e) {
      console.error('Error normalizing year:', e);
    }
    return null;
  }

  populateFormForEdit(): void {
    // Populate form with existing rowData - include ALL form fields
    this.thresholdEntity = {
      TABLE_ID: this.rowData.TABLE_ID || "",
      TAX_YEAR: this.rowData.TAX_YEAR || this.filterParams.tax_year,
      SCHEMA_OWNER: this.rowData.SCHEMA_OWNER || "GTWFDM01",
      TABLE_NAME: this.rowData.TABLE_NAME || "",
      REFRESH_TYPE: this.rowData.REFRESH_TYPE || "",
      FROM_TAX_YEAR: this.rowData.FROM_TAX_YEAR || "",
      TO_TAX_YEAR: this.rowData.TO_TAX_YEAR || "",
      TABLE_CLASSIFICATION: this.rowData.TABLE_CLASSIFICATION || "",
      SOURCE_SCHEMA: this.rowData.SOURCE_SCHEMA || "GTWFDM01",
      SOURCE_TABLE_NAME: this.rowData.SOURCE_TABLE_NAME || "",
      TABLE_TYPE: this.rowData.TABLE_TYPE || "",
      SRC_TO_TGT_VAR_THRESHOLD: this.rowData.SRC_TO_TGT_VAR_THRESHOLD || "",
      CLASSIFICATION_KEY: this.rowData.CLASSIFICATION_KEY || "",
      CLASSIFICATION_DESC: this.rowData.CLASSIFICATION_DESC || ""
    };

    // Set Year Picker values for edit mode using normalizeYear function
    if (this.rowData.FROM_TAX_YEAR) {
      const fromYear = this.normalizeYear(this.rowData.FROM_TAX_YEAR);
      if (fromYear !== null) {
        this.fromTaxYear = fromYear;
        // Update thresholdEntity with normalized year value
        this.thresholdEntity.FROM_TAX_YEAR = fromYear.toString();
      }
    }
    if (this.rowData.TO_TAX_YEAR) {
      const toYear = this.normalizeYear(this.rowData.TO_TAX_YEAR);
      if (toYear !== null) {
        this.toTaxYear = toYear;
        // Update thresholdEntity with normalized year value
        this.thresholdEntity.TO_TAX_YEAR = toYear.toString();
      }
    }

    // Update form controls
    this.thresholdForm.patchValue(this.thresholdEntity);
    
    // Ensure year picker values are reflected in form controls
    if (this.fromTaxYear) {
      this.thresholdForm.get('FROM_TAX_YEAR')?.setValue(this.fromTaxYear.toString());
    }
    if (this.toTaxYear) {
      this.thresholdForm.get('TO_TAX_YEAR')?.setValue(this.toTaxYear.toString());
    }
    
    // Explicitly set REFRESH_TYPE to ensure dropdown binding works in edit mode
    if (this.thresholdEntity.REFRESH_TYPE) {
      this.thresholdForm.get('REFRESH_TYPE')?.setValue(this.thresholdEntity.REFRESH_TYPE);
    }
    
    // Explicitly set TABLE_TYPE to ensure dropdown binding works in edit mode
    if (this.thresholdEntity.TABLE_TYPE) {
      this.thresholdForm.get('TABLE_TYPE')?.setValue(this.thresholdEntity.TABLE_TYPE);
    }
    
    // Explicitly set TABLE_CLASSIFICATION to ensure dropdown binding works in edit mode
    if (this.thresholdEntity.TABLE_CLASSIFICATION) {
      this.thresholdForm.get('TABLE_CLASSIFICATION')?.setValue(this.thresholdEntity.TABLE_CLASSIFICATION);
    }
    
    // 🚀 CRITICAL: Store backup after all values are set (like reference-table component)
    this.thresholdEntity_bckup = _.cloneDeep(this.thresholdEntity);
    
    // 🚀 CRITICAL: Also store year picker values in backup for reset functionality
    // Store the normalized year values that were set
    // if (this.fromTaxYear !== null && this.fromTaxYear !== undefined) {
    //   this.thresholdEntity_bckup.FROM_TAX_YEAR = this.fromTaxYear.toString();
    // }
    // if (this.toTaxYear !== null && this.toTaxYear !== undefined) {
    //   this.thresholdEntity_bckup.TO_TAX_YEAR = this.toTaxYear.toString();
    // }
  }

  populateFormForCreate(): void {
    // Set default values for create mode
    const currentYear = new Date().getFullYear();
    // 🚀 CRITICAL: Use filterParams first, then fallback to globalService
    const taxYear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || currentYear.toString();
    const taxYearNum = parseInt(taxYear) || currentYear;
    
    this.thresholdEntity_bckup = {
      TAX_YEAR: taxYear,
      TABLE_ID: '',
      SCHEMA_OWNER: 'GTWFDM01',
      TABLE_NAME: '',
      REFRESH_TYPE: '',
      FROM_TAX_YEAR: (taxYearNum).toString(),
      TO_TAX_YEAR: '9999',
      TABLE_CLASSIFICATION: '',
      SOURCE_SCHEMA: 'GTWFDM01',
      SOURCE_TABLE_NAME: '',
      TABLE_TYPE: 'TRANSACTIONAL',
      SRC_TO_TGT_VAR_THRESHOLD: '',
      CLASSIFICATION_KEY: '',
      CLASSIFICATION_DESC: ''
    };

    this.thresholdEntity = _.merge({}, this.thresholdEntity_bckup);

    // Set Year Picker default values for create mode
    this.fromTaxYear = taxYearNum;
    this.toTaxYear = 9999;

    // Update form controls
    this.thresholdForm.patchValue(this.thresholdEntity);
    
    // Ensure year picker values are reflected in form controls
    if (this.fromTaxYear) {
      this.thresholdForm.get('FROM_TAX_YEAR')?.setValue(this.fromTaxYear.toString());
    }
    if (this.toTaxYear) {
      this.thresholdForm.get('TO_TAX_YEAR')?.setValue(this.toTaxYear.toString());
    }
  }

  loadThresholdRefreshType(): void {
    this.ecThresholdSetupService.loadThresholdRefreshType(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (refreshType: any[]) => {
        console.log("load all Refresh Type", refreshType);
        this.allRefreshTypes = refreshType;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error("Error loading refresh types:", error);
        this.gtwSnackbarService.openSnackBar('Error loading refresh types', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  loadThresholdClassification(): void {
    this.ecThresholdSetupService.loadThresholdClassification(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (classificationKey: any[]) => {
        console.log("load all Classification", classificationKey);
        this.allTableClasses = classificationKey;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error("Error loading classifications:", error);
        this.gtwSnackbarService.openSnackBar('Error loading classifications', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  loadThresholdTableType(): void {
    this.ecThresholdSetupService.loadThresholdTableType(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (tableType: any[]) => {
        console.log("load all Table Type", tableType);
        this.allTableTypes = tableType;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error("Error loading table types:", error);
        this.gtwSnackbarService.openSnackBar('Error loading table types', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  loadThresholdValue(): void {
    this.ecThresholdSetupService.loadThresholdValue(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (thresholdValue: any[]) => {
        console.log("load all Threshold Value", thresholdValue);
        this.allThresholdValues = thresholdValue;
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error("Error loading threshold values:", error);
        this.gtwSnackbarService.openSnackBar('Error loading threshold values', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  toggleMinimize(): void {
    this.minimise = !this.minimise;
  }

  // Validation method
  isValid(): boolean {
    // Reset validation flags
    this.thresholdEntity.TAX_YEAR_NOT_VALID = false;
    this.thresholdEntity.SCHEMA_OWNER_NOT_VALID = false;
    this.thresholdEntity.TABLE_NAME_NOT_VALID = false;
    this.thresholdEntity.REFRESH_TYPE_NOT_VALID = false;
    this.thresholdEntity.SOURCE_SCHEMA_NOT_VALID = false;
    this.thresholdEntity.SOURCE_TABLE_NAME_NOT_VALID = false;
    this.thresholdEntity.TABLE_TYPE_NOT_VALID = false;
    this.thresholdEntity.FROM_TAX_YEAR_NOT_VALID = false;
    this.thresholdEntity.TO_TAX_YEAR_NOT_VALID = false;
    this.thresholdEntity.TABLE_CLASSIFICATION_NOT_VALID = false;
    this.thresholdEntity.SRC_TO_TGT_VAR_THRESHOLD_NOT_VALID = false;
    this.thresholdEntity.CLASSIFICATION_KEY_NOT_VALID = false;

    // Get current form values
    const formValues = this.thresholdForm.value;

    if (!formValues.TAX_YEAR) {
      this.thresholdEntity.TAX_YEAR_NOT_VALID = true;
      return false;
    }
    if (!formValues.SCHEMA_OWNER) {
      this.thresholdEntity.SCHEMA_OWNER_NOT_VALID = true;
      return false;
    }
    if (!formValues.TABLE_NAME) {
      this.thresholdEntity.TABLE_NAME_NOT_VALID = true;
      return false;
    }
    if (!formValues.REFRESH_TYPE) {
      this.thresholdEntity.REFRESH_TYPE_NOT_VALID = true;
      return false;
    }
    if (!formValues.SOURCE_SCHEMA) {
      this.thresholdEntity.SOURCE_SCHEMA_NOT_VALID = true;
      return false;
    }
    if (!formValues.SOURCE_TABLE_NAME) {
      this.thresholdEntity.SOURCE_TABLE_NAME_NOT_VALID = true;
      return false;
    }
    if (!formValues.TABLE_TYPE) {
      this.thresholdEntity.TABLE_TYPE_NOT_VALID = true;
      return false;
    }
    if (!formValues.FROM_TAX_YEAR) {
      this.thresholdEntity.FROM_TAX_YEAR_NOT_VALID = true;
      return false;
    }
    if (!formValues.TO_TAX_YEAR) {
      this.thresholdEntity.TO_TAX_YEAR_NOT_VALID = true;
      return false;
    }
    // 🚀 CRITICAL: Create mode validates CLASSIFICATION_KEY, Update mode validates TABLE_CLASSIFICATION
    // Both use the same form control (TABLE_CLASSIFICATION) which stores CLASSIFICATION_KEY value
    if (this.rowData && this.rowData.TABLE_ID) {
      // Update mode - validate TABLE_CLASSIFICATION
      if (!formValues.TABLE_CLASSIFICATION) {
        this.thresholdEntity.TABLE_CLASSIFICATION_NOT_VALID = true;
        return false;
      }
    } else {
      // Create mode - validate CLASSIFICATION_KEY (stored in TABLE_CLASSIFICATION form control)
      if (!formValues.TABLE_CLASSIFICATION) {
        this.thresholdEntity.CLASSIFICATION_KEY_NOT_VALID = true;
        return false;
      }
    }
    
    if (!formValues.SRC_TO_TGT_VAR_THRESHOLD) {
      this.thresholdEntity.SRC_TO_TGT_VAR_THRESHOLD_NOT_VALID = true;
      return false;
    }

    return true;
  }

  // CRUD operations
  save(): void {
    if (!this.isValid()) {
      this.gtwSnackbarService.openSnackBar('Please fill all the required fields', 'gtw-snackbar--red');
      return;
    }
    
    this.isSaving = true;
    
    // Build json_data payload
    // 🚀 CRITICAL: Use filterParams first, then fallback to globalService
    const formValue = this.thresholdForm.value;
    
    // 🚀 CRITICAL: Extract CLASSIFICATION_KEY and CLASSIFICATION_DESC from selected classification
    // TABLE_CLASSIFICATION form control contains the CLASSIFICATION_KEY value
    const selectedClassificationKey = formValue.TABLE_CLASSIFICATION || '';
    let classificationDesc = '';
    
    // Find the selected classification to get CLASSIFICATION_DESC
    if (selectedClassificationKey && this.allTableClasses.length > 0) {
      const selectedClassification = this.allTableClasses.find(
        (item: any) => item.CLASSIFICATION_KEY === selectedClassificationKey
      );
      if (selectedClassification) {
        classificationDesc = selectedClassification.CLASSIFICATION_DESC || '';
      }
    }
    
    const jsonData = {
      TAX_YEAR: formValue.TAX_YEAR || this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '2024',
      TABLE_ID: formValue.TABLE_ID || '',
      SCHEMA_OWNER: formValue.SCHEMA_OWNER || 'GTWFDM01',
      TABLE_NAME: formValue.TABLE_NAME || '',
      REFRESH_TYPE: formValue.REFRESH_TYPE || '',
      FROM_TAX_YEAR: this.fromTaxYear ? this.fromTaxYear.toString() : formValue.FROM_TAX_YEAR || '',
      TO_TAX_YEAR: this.toTaxYear ? this.toTaxYear.toString() : formValue.TO_TAX_YEAR || '',
      TABLE_CLASSIFICATION: formValue.TABLE_CLASSIFICATION || '',
      SOURCE_SCHEMA: formValue.SOURCE_SCHEMA || 'GTWFDM01',
      SOURCE_TABLE_NAME: formValue.SOURCE_TABLE_NAME || '',
      TABLE_TYPE: formValue.TABLE_TYPE || '',
      SRC_TO_TGT_VAR_THRESHOLD: formValue.SRC_TO_TGT_VAR_THRESHOLD || '',
      CLASSIFICATION_DESC: classificationDesc,
      CLASSIFICATION_KEY: selectedClassificationKey,
      FROM_TAX_YEAR_CLICK: false,
      tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '2024',
      scenario: parseInt(this.filterParams?.scenario || this.globalService?.globalParams?.scenario || '74')
    };

    // Build the complete payload structure
    // 🚀 CRITICAL: Use filterParams first, then fallback to globalService
    const payload = {
      jsonSettings: JSON.stringify({
        tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '2024'
      }),
      json_data: JSON.stringify(jsonData),
      tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '2024',
      screen_key: this.filterParams?.screen_key || this.globalService?.globalParams?.screen_key || '',
      scenario: this.filterParams?.scenario || this.globalService?.globalParams?.scenario || '74',
      jcd_key: this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '250',
      jcd_ta_key: this.filterParams?.jcd_ta_key || this.globalService?.globalParams?.jcd_ta_key || '',
      activity_key: this.filterParams?.activity_key || this.globalService?.globalParams?.activity_key || '',
      screen_type: 'grid',
      is_locked: 'N',
      issue_id: this.filterParams?.issue_id || this.globalService?.globalParams?.issue_id || '',
      issue_short_desc: this.filterParams?.issue_short_desc || this.globalService?.globalParams?.issue_short_desc || '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save || this.globalService?.globalParams?.issue_id_for_tx_save || '',
      scenarioCode: this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.globalParams?.scenarioCode || this.globalService?.globalParams?.scenario_code || 'RAF',
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled || this.globalService?.globalParams?.is_issue_key_enabled || 'N',
      checkLockedbj: JSON.stringify([{
        scenario_key: String(this.filterParams?.scenario || this.globalService?.globalParams?.scenario || '74')
      }])
    };
    
    this.ecThresholdSetupService.saveThresholdForm(
      this.GENERAL_CONFIG.base_url,
      payload,
      this.globalService?.globalParams ?? {},
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isSaving = false;
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this.gtwSnackbarService.openSnackBar('Threshold Management created successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Failed to save threshold setup', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isSaving = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        console.error('Save error:', error);
        this.cdr.markForCheck();
      }
    });
  }

  update(): void {
    if (!this.isValid()) {
      this.gtwSnackbarService.openSnackBar('Please fill all the required fields', 'gtw-snackbar--red');
      return;
    }
    
    this.isSavingUpdate = true;
    
    // Build json_data payload for update
    const formValue = this.thresholdForm.value;
    
    // Use Year Picker values as strings
    const fromTaxYearStr = this.fromTaxYear ? this.fromTaxYear.toString() : (formValue.FROM_TAX_YEAR || '');
    const toTaxYearStr = this.toTaxYear ? this.toTaxYear.toString() : (formValue.TO_TAX_YEAR || '');
    
    // TABLE_CLASSIFICATION should be a number (parseInt)
    const tableClassification = formValue.TABLE_CLASSIFICATION ? parseInt(formValue.TABLE_CLASSIFICATION) : 0;
    
    // TABLE_ID should be a number
    const tableId = formValue.TABLE_ID || this.rowData?.TABLE_ID;
    const tableIdNum = tableId ? parseInt(tableId) : 0;
    
    // SRC_TO_TGT_VAR_THRESHOLD should be a number (can be decimal)
    const thresholdValue = formValue.SRC_TO_TGT_VAR_THRESHOLD || '';
    const thresholdValueNum = thresholdValue ? parseFloat(thresholdValue) : 0;
    
    // Build json_data with all required fields
    const jsonData = {
      TABLE_ID: tableIdNum,
      SCHEMA_OWNER: formValue.SCHEMA_OWNER || 'GTWFDM01',
      TABLE_NAME: formValue.TABLE_NAME || '',
      REFRESH_TYPE: formValue.REFRESH_TYPE || '',
      FROM_TAX_YEAR: fromTaxYearStr,
      TO_TAX_YEAR: toTaxYearStr,
      TABLE_CLASSIFICATION: tableClassification,
      SOURCE_SCHEMA: formValue.SOURCE_SCHEMA || 'GTWFDM01',
      SOURCE_TABLE_NAME: formValue.SOURCE_TABLE_NAME || '',
      TABLE_TYPE: formValue.TABLE_TYPE || '',
      SRC_TO_TGT_VAR_THRESHOLD: thresholdValueNum,
      object_id: this.rowData?.object_id || this.rowData?.OBJECT_ID || '1',
      SCENARIO_TYPE_CODE: this.rowData?.SCENARIO_TYPE_CODE || '',
      tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '2024',
      scenario: parseInt(this.filterParams?.scenario || this.globalService?.globalParams?.scenario || '74')
    };
    
    // Build complete payload with all required parameters
    // 🚀 CRITICAL: Use filterParams first, then fallback to globalService
    const params: any = {
      json_data: JSON.stringify(jsonData),
      tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '2024',
      screen_key: this.filterParams?.screen_key || this.globalService?.globalParams?.screen_key || '',
      scenario: this.filterParams?.scenario || this.globalService?.globalParams?.scenario || '74',
      jcd_key: this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key || '250',
      jcd_ta_key: this.filterParams?.jcd_ta_key || this.globalService?.globalParams?.jcd_ta_key || '',
      activity_key: this.filterParams?.activity_key || this.globalService?.globalParams?.activity_key || '',
      screen_type: 'grid',
      is_locked: 'N',
      issue_id: this.filterParams?.issue_id || this.globalService?.globalParams?.issue_id || '',
      issue_short_desc: this.filterParams?.issue_short_desc || this.globalService?.globalParams?.issue_short_desc || '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save || this.globalService?.globalParams?.issue_id_for_tx_save || '',
      checkLockedbj: JSON.stringify([{
        scenario_key: String(this.filterParams?.scenario || this.globalService?.globalParams?.scenario || '74')
      }]),
      scenarioCode: this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.globalParams?.scenarioCode || this.globalService?.globalParams?.scenario_code || 'RAF',
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled || this.globalService?.globalParams?.is_issue_key_enabled || 'N'
    };
    
    this.ecThresholdSetupService.updateThresholdForm(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService?.globalParams ?? {},
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isSavingUpdate = false;
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this.gtwSnackbarService.openSnackBar('Threshold Management updated successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Failed to update threshold setup', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isSavingUpdate = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while updating. Please try again.', 'gtw-snackbar--red');
        console.error('Update error:', error);
        this.cdr.markForCheck();
      }
    });
  }

  delete(): void {
    if (!this.rowData.TABLE_ID) {
      this.gtwSnackbarService.openSnackBar('Missing required data for deletion', 'gtw-snackbar--red');
      return;
    }

    this.isSavingDelete = true;
    
    this.ecThresholdSetupService.deleteThreshold(
      this.GENERAL_CONFIG.base_url,
      this.rowData.TABLE_ID,
      this.globalService?.globalParams ?? {},
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isSavingDelete = false;
        if (response.callSuccess === "1" && response.errorMessage === "null") {
          this.gtwSnackbarService.openSnackBar('Threshold Management deleted successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Failed to delete threshold setup', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.isSavingDelete = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
        console.error('Delete error:', error);
        this.cdr.markForCheck();
      }
    });
  }

  reset(): void {
    // 🚀 CRITICAL: Use reset() method instead of patchValue() to properly reset ALL form fields
    // Reset thresholdEntity to backup values
    this.thresholdEntity = _.cloneDeep(this.thresholdEntity_bckup);
    
    // Reset Year Picker values based on backup
    if (this.rowData && this.rowData.TABLE_ID) {
      // Edit mode - reset to original values from backup
      if (this.thresholdEntity_bckup.FROM_TAX_YEAR) {
        const fromYear = this.normalizeYear(this.thresholdEntity_bckup.FROM_TAX_YEAR);
        if (fromYear !== null) {
          this.fromTaxYear = fromYear;
        }
      }
      if (this.thresholdEntity_bckup.TO_TAX_YEAR) {
        const toYear = this.normalizeYear(this.thresholdEntity_bckup.TO_TAX_YEAR);
        if (toYear !== null) {
          this.toTaxYear = toYear;
        }
      }
    } else {
      // Create mode - reset to default values from backup
      if (this.thresholdEntity_bckup.FROM_TAX_YEAR) {
        const fromYear = this.normalizeYear(this.thresholdEntity_bckup.FROM_TAX_YEAR);
        if (fromYear !== null) {
          this.fromTaxYear = fromYear;
        }
      }
      if (this.thresholdEntity_bckup.TO_TAX_YEAR) {
        const toYear = this.normalizeYear(this.thresholdEntity_bckup.TO_TAX_YEAR);
        if (toYear !== null) {
          this.toTaxYear = toYear;
        }
      }
    }
    
    // 🚀 CRITICAL: Use reset() method to properly reset ALL form fields to backup values
    // This ensures all fields (not just FROM_TAX_YEAR and TO_TAX_YEAR) are reset
    this.thresholdForm.reset(this.thresholdEntity);
    
    // Ensure year picker values are reflected in form controls after reset
    if (this.fromTaxYear) {
      this.thresholdForm.get('FROM_TAX_YEAR')?.setValue(this.fromTaxYear.toString());
    }
    if (this.toTaxYear) {
      this.thresholdForm.get('TO_TAX_YEAR')?.setValue(this.toTaxYear.toString());
    }
    
    // Explicitly reset dropdown fields to ensure they're properly reset
    if (this.thresholdEntity.REFRESH_TYPE) {
      this.thresholdForm.get('REFRESH_TYPE')?.setValue(this.thresholdEntity.REFRESH_TYPE);
    }
    if (this.thresholdEntity.TABLE_TYPE) {
      this.thresholdForm.get('TABLE_TYPE')?.setValue(this.thresholdEntity.TABLE_TYPE);
    }
    if (this.thresholdEntity.TABLE_CLASSIFICATION) {
      this.thresholdForm.get('TABLE_CLASSIFICATION')?.setValue(this.thresholdEntity.TABLE_CLASSIFICATION);
    }
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  trackByIndex(index: number, item: any): any {
    return index;
  }

  // Year Picker methods
  openFromYearPicker(): void {
    // Close the To Year picker if it's open
    if (this.showToYearPicker) {
      this.showToYearPicker = false;
    }
    this.showFromYearPicker = true;
  }

  closeFromYearPicker(): void {
    this.showFromYearPicker = false;
  }

  onFromYearSelected(year: number | null): void {
    if (year === null) {
      // Clear the year
      this.fromTaxYear = null;
      this.thresholdForm.get('FROM_TAX_YEAR')?.setValue('');
      this.thresholdEntity.FROM_TAX_YEAR = '';
    } else {
      // Set the year directly (picker already returns the correct year)
      this.fromTaxYear = year;
      this.thresholdForm.get('FROM_TAX_YEAR')?.setValue(year.toString());
      this.thresholdEntity.FROM_TAX_YEAR = year.toString();
      
      // Validate that From Tax Year is not greater than To Tax Year
      if (this.toTaxYear && year > this.toTaxYear) {
        this.gtwSnackbarService.openSnackBar('From Tax Year cannot be greater than To Tax Year', 'gtw-snackbar--red');
        this.fromTaxYear = null;
        this.thresholdForm.get('FROM_TAX_YEAR')?.setValue('');
        this.thresholdEntity.FROM_TAX_YEAR = '';
      }
    }
    
    // Update the thresholdEntity to reflect the current form state
    this.thresholdEntity = { ...this.thresholdEntity, ...this.thresholdForm.value };
  }

  openToYearPicker(): void {
    // Close the From Year picker if it's open
    if (this.showFromYearPicker) {
      this.showFromYearPicker = false;
    }
    this.showToYearPicker = true;
  }

  closeToYearPicker(): void {
    this.showToYearPicker = false;
  }

  onToYearSelected(year: number | null): void {
    if (year === null) {
      // Clear the year
      this.toTaxYear = null;
      this.thresholdForm.get('TO_TAX_YEAR')?.setValue('');
      this.thresholdEntity.TO_TAX_YEAR = '';
    } else {
      // Set the year directly (picker already returns the correct year)
      this.toTaxYear = year;
      this.thresholdForm.get('TO_TAX_YEAR')?.setValue(year.toString());
      this.thresholdEntity.TO_TAX_YEAR = year.toString();
      
      // Validate that To Tax Year is not less than From Tax Year
      if (this.fromTaxYear && year < this.fromTaxYear) {
        this.gtwSnackbarService.openSnackBar('To Tax Year cannot be less than From Tax Year', 'gtw-snackbar--red');
        this.toTaxYear = null;
        this.thresholdForm.get('TO_TAX_YEAR')?.setValue('');
        this.thresholdEntity.TO_TAX_YEAR = '';
      }
    }
    
    // Update the thresholdEntity to reflect the current form state
    this.thresholdEntity = { ...this.thresholdEntity, ...this.thresholdForm.value };
  }
}